import { useEffect, useMemo, useState } from "react";

// MUI
import { SxProps, Typography } from "@mui/material";
import { GridColDef, useGridApiRef } from "@mui/x-data-grid-premium";
import { GRID_CHECKBOX_SELECTION_COL_DEF, GridRowSelectionModel, GridValidRowModel } from "@mui/x-data-grid-pro";

// Third-party
import clsx from "clsx";

// Components
import DataSmartTable from "@/Layouts/components/Tables/DataSmartTable";
import { TypographyHeader } from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyHeader";
import TypographyCell from "@/Layouts/components/Tables/DataSmartTable/additional/TypographyCell";
import NoRowsOverlay from "@/Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import CommonToolbar from "@/Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import EmailsRenderCell from "@/DataPages/components/common/EmailsRenderCell";
import PhoneNumbersRenderCell from "@/DataPages/components/common/PhoneNumbersRenderCell";
import ProspectNoteRenderCell from "@/DataPages/components/CRM/SalesLeads/ProspectNoteDialog/ProspectNoteRenderCell";
import ProspectNoteDialog from "../ProspectNoteDialog/ProspectNoteDialog";
import TableCustomComponents from "@/DataPages/components/common/PaginationHeaderLoading";
import ArrayDialog from "@/DataPages/components/common/ListColumnDialog/ArrayDialog";

// Helpers
import { getPinnedCellClassName, generateSx } from "@Layouts/components/Tables/DataSmartTable/helpers";
import { getLastActivityInfo, getProspectTypeIcon, getResponseStatusInfo } from "@/DataPages/helpers/crm/utils";
import { formatPhoneNumber } from "@/Common/helpers/Modifiers/formatters";
import { getFormatUTCDate } from "@/Common/helpers/Dates";

// Types
import { Nullable } from "@/Common/interfaces/types";
import { DataSmartTableProps } from "@/Layouts/components/types";
import { IDesiredVehicle, IMailDetails, IPhoneDetails, IPotentialTrade, ISalesLeads } from "@/DataPages/types/rci/salesLeads/model";
import { IArrayDialogContent, IProspectNoteDialogContent } from "../types/salesLeads";
import { MappingDialogContent } from "@/Common/types/meta/mappingFilters";

// Constants
import {
	EIGHTY,
	FIFTY_FIVE,
	FIFTY_ONE,
	FIFTY_TWO,
	HEADER_CLASS_NAME,
	ONE_HUNDRED,
	ONE_HUNDRED_FIFTEEN,
	ONE_HUNDRED_FIFTY,
	ONE_HUNDRED_SEVENTEEN,
	SEVENTY,
	SEVENTY_EIGHT,
	SIXTY,
	TAILWIND_FOR_DATA_SMART_TABLE,
	TWELVE,
} from "@/Constants/tables";
import { BASE_TABLE_PROPS, DEFAULT_HEADER_HEIGHT, DEFAULT_ROW_HEIGHT, ID_GRID_COLDEF } from "@/Washouts/constants";
import { DATE_FORMAT } from "@/Constants/date";

const maxRows = 10;

const slots = {
	toolbar: CommonToolbar,
	noRowsOverlay: NoRowsOverlay,
	noResultsOverlay: NoRowsOverlay,
};

function useCustomProps(params: { rows: readonly GridValidRowModel[] }): {
	customProps: DataSmartTableProps["customProps"];
} {
	const { rows } = params ?? {};
	const customProps = useMemo(() => {
		return {
			...BASE_TABLE_PROPS,
			tableContainerClassName: clsx("[&_.MuiDataGrid-main]:mt-2 after:[&_.MuiDataGrid-topContainer]:!bg-disabled !h-full", {
				"[&_.toolbar-actions]:!absolute [&_.toolbar-actions]:!right-[0px]": true,
			}),
			tableClassName: {
				"functional-table display-select-all-checkbox": true,
				"[&_.MuiDataGrid-columnHeader--withRightBorder]:!border-none": true,
				"[&_.MuiDataGrid-filler--pinnedLeft]:!border-r-transparent": true,
				"[&_.MuiDataGrid-row:not(.footer):hover_.MuiDataGrid-cell--pinnedLeft:not(.empty-row,.group-child)]:shadow-table-hov": true,
				"[&_.MuiDataGrid-columnHeader_.MuiCheckbox-root]:!mr-[10px]": true,
				"[&_.MuiDataGrid-columnHeader_.MuiCheckbox-root]:ml-[4px]": true,
				"[&_.MuiDataGrid-columnHeader_.MuiCheckbox-root>.MuiTouchRipple-root]:!invisible": true,
				...TAILWIND_FOR_DATA_SMART_TABLE,
			},
			aggregationFooterClassName: "aggregation-footer regular-footer",
			expandableRows: false,
			hide: !rows?.length,
		};
	}, []);
	return { customProps };
}

const columnVisibilityModel = {
	_id: false,
};

const baseColumnDefinition: Partial<GridColDef> = {
	headerClassName: HEADER_CLASS_NAME,
	renderCell: (params) => <TypographyCell params={params} />,
	renderHeader: (params) => <TypographyHeader params={params} />,
};

const getBaseColumnDefinition = (width?: number) => {
	if (!width) {
		return baseColumnDefinition;
	}

	return {
		...baseColumnDefinition,
		width,
		minWidth: width,
		maxWidth: width,
	};
};

const PopupWrapper = ({ text, data, onClick }: { text: string; data: object[]; onClick: () => void }) => {
	if (!data?.length) {
		return null;
	}
	return (
		<Typography
			className="!text-primary !font-sans underline decoration-primary cursor-pointer !text-[12px] pt-[3px]"
			onClick={onClick}
		>
			{text}
		</Typography>
	);
};

const getPreProspectNoteColumnsDef = (): GridColDef[] => {
	return [
		ID_GRID_COLDEF,
		{
			field: GRID_CHECKBOX_SELECTION_COL_DEF.field,
			width: 60,
			minWidth: 60,
			maxWidth: 60,
			sortable: false,
			headerClassName: clsx(HEADER_CLASS_NAME, {
				"[&_.MuiCheckbox-root]:relative [&_.MuiCheckbox-root]:left-[-4px]": true,
			}),
			cellClassName: (params) => getPinnedCellClassName(params),
		},
		{
			...getBaseColumnDefinition(FIFTY_TWO),
			type: "singleSelect",
			field: "prospectType",
			headerName: "Type",
			align: "center",
			cellClassName: (params) => getPinnedCellClassName(params),
			valueOptions: ["Internet", "Phone", "Walk-In", "Other", "List", "Fax", "DMS Created"],
			renderCell: (params) => {
				const { value } = params;
				const icon = getProspectTypeIcon(value);
				return (
					<TypographyCell
						tooltipProps={{ title: value }}
						forceTooltip={true}
						className="hover:cursor-pointer flex justify-center"
					>
						<img src={icon} alt={`Prospect Type`} className="hover:cursor-pointer py-1" />
					</TypographyCell>
				);
			},
		},
		{
			...getBaseColumnDefinition(FIFTY_FIVE),
			field: "responseStatus",
			headerName: "Resp.",
			type: "singleSelect",
			valueOptions: ["Contacted", "No Contacted", "Overdue"],
			valueGetter: (responseStatus) => {
				const statusMap = { CONTACTED: "Contacted", NOT_CONTACTED: "No Contacted", OVERDUE: "Overdue" };
				return statusMap[responseStatus] ?? responseStatus;
			},
			cellClassName: (params) => getPinnedCellClassName(params),
			renderCell: ({ row }) => {
				const { icon, title } = getResponseStatusInfo(row.responseStatus);
				if (icon) {
					return (
						<TypographyCell tooltipProps={{ title }} forceTooltip={true} className="hover:cursor-pointer">
							<img src={icon} alt={`Prospect Contacted`} className="hover:cursor-pointer py-1" />
						</TypographyCell>
					);
				}
				return "";
			},
		},
		{
			...getBaseColumnDefinition(FIFTY_ONE),
			field: "hasOEMEvent",
			headerName: "OEM",
			type: "singleSelect",
			valueOptions: ["Yes", "No"],
			cellClassName: (params) => getPinnedCellClassName(params),
			valueGetter: (value) => (value ? "Yes" : "No"),
			renderCell: ({ row }) => <TypographyCell>{row.hasOEMEvent ? "Yes" : "No"}</TypographyCell>,
		},
		{
			...getBaseColumnDefinition(),
			field: "customerName",
			headerName: "Customer Name",
			cellClassName: (params) => getPinnedCellClassName(params),
		},
		{
			...getBaseColumnDefinition(SEVENTY_EIGHT),
			field: "prospectCategory",
			headerName: "Category",
			cellClassName: (params) => getPinnedCellClassName(params),
		},
		{
			...getBaseColumnDefinition(EIGHTY),
			field: "leadDate",
			headerName: "Lead Date",
			type: "dateTime",
			valueGetter: (value, row) => new Date(value ?? row.createdAt),
			valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.monthAbbrDatePadded),
			cellClassName: (params) => getPinnedCellClassName(params),
		},
		{
			...getBaseColumnDefinition(EIGHTY),
			field: "updatedAt",
			headerName: "Last Modified",
			type: "dateTime",
			valueGetter: (value, row) => new Date(value ?? row.updatedAt),
			valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.monthAbbrDatePadded),
			cellClassName: (params) => getPinnedCellClassName(params),
		},
		{
			...getBaseColumnDefinition(SEVENTY),
			field: "prospectStatus",
			headerName: "Status",
			cellClassName: (params) => getPinnedCellClassName(params),
		},
		{
			...getBaseColumnDefinition(ONE_HUNDRED_FIFTEEN),
			field: "inStoreDate",
			headerName: "In Store",
			type: "dateTime",
			cellClassName: (params) => getPinnedCellClassName(params),
			valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.usDateTime12hPadded),
		},
		{
			...getBaseColumnDefinition(EIGHTY),
			field: "bdcUser",
			headerName: "BDC User",
			cellClassName: (params) => getPinnedCellClassName(params),
		},
		{
			...getBaseColumnDefinition(EIGHTY),
			field: "prospectId",
			headerName: "Prospect ID",
			valueGetter: (_, row) => row?.prospectId,
			cellClassName: (params) => getPinnedCellClassName(params),
		},
		{
			...getBaseColumnDefinition(SIXTY),
			field: "nameRecId",
			headerName: "DMS ID #",
			cellClassName: (params) => getPinnedCellClassName(params),
		},
		{
			...getBaseColumnDefinition(TWELVE),
			field: "__empty",
			headerName: "",
			headerClassName: "!z-10 !bg-white !border-none !p-0",
			cellClassName: () => "empty-row !bg-white !border-none !p-0",
		},
		{
			...getBaseColumnDefinition(ONE_HUNDRED),
			field: "manager",
			headerName: "Manager",
			valueGetter: (_, row) => row?.manager,
		},
		{
			...getBaseColumnDefinition(),
			field: "primarySalesPerson",
			headerName: "Salesperson",
			valueGetter: (_, row) => row?.primarySalesPerson,
		},
		{
			...getBaseColumnDefinition(ONE_HUNDRED),
			field: "secondarySalesPerson",
			headerName: "Secondary Salesperson",
			valueGetter: (_, row) => row?.secondarySalesPerson,
		},
		{
			...getBaseColumnDefinition(ONE_HUNDRED),
			field: "providerName",
			headerName: "Source",
		},
		{
			...getBaseColumnDefinition(ONE_HUNDRED_FIFTY),
			field: "phoneNumbers",
			headerName: "Phone(s)",
			valueGetter: (value) => {
				if (!Array.isArray(value)) {
					return "";
				}
				return (value as IPhoneDetails[]).map((phone) => {
					const type = phone?.type ? `(${phone?.type})` : "";
					const formattedPhone = formatPhoneNumber(phone?.number, false);
					const ext = phone?.extension ? `ext. ${phone?.extension}` : "";
					return `${type} ${formattedPhone} ${ext}`;
				});
			},
			renderCell: (params) => <PhoneNumbersRenderCell params={params} />,
		},
		{
			...getBaseColumnDefinition(ONE_HUNDRED_FIFTY),
			field: "email",
			headerName: "Email(s)",
			valueGetter: (value) => {
				if (!Array.isArray(value)) {
					return "";
				}
				return (value as IMailDetails[]).map((emailItem) => {
					return emailItem?.mailTo;
				});
			},
			renderCell: (params) => {
				return <EmailsRenderCell params={params} />;
			},
		},
	];
};

const getPostProspectNoteColumnsDef = ({
	setProspectNoteDialog,
	setArrayModalContent,
}: {
	setProspectNoteDialog: ({ title, data }: IProspectNoteDialogContent) => void;
	setArrayModalContent: ({ title, data }: IArrayDialogContent) => void;
}): GridColDef[] => {
	return [
		{
			...getBaseColumnDefinition(ONE_HUNDRED_SEVENTEEN),
			field: "prospectNote",
			headerName: "Prospect Note",
			renderCell: (params) => {
				const { row } = params;
				const customerName = row?.customerName ?? "";
				const title = `${customerName} Prospect Note`;
				return ProspectNoteRenderCell({
					cellText: "View Note(s)",
					value: params.value,
					title,
					setContentDialogSelected: setProspectNoteDialog,
				});
			},
		},
		{
			...getBaseColumnDefinition(ONE_HUNDRED_FIFTY),
			field: "desiredVehicle",
			headerName: "Desired Vehicle",
			valueGetter: (value: IDesiredVehicle) => `${value?.vehicleYear ?? ""} ${value?.vehicleMake ?? ""} ${value?.vehicleModel ?? ""}`,
			renderCell: (params) => {
				const { row } = params;
				const { desiredVehicle } = row;

				return (
					<PopupWrapper
						text="View Desired Vehicle"
						data={desiredVehicle}
						onClick={() => {
							setArrayModalContent({ title: "Desired Vehicle", data: desiredVehicle });
						}}
					/>
				);
			},
		},
		{
			...getBaseColumnDefinition(ONE_HUNDRED_FIFTY),
			field: "potentialTrade",
			headerName: "Trade Vehicle",
			valueGetter: (value: IPotentialTrade) =>
				`${value?.tradeVehicleYear ?? ""} ${value?.tradeVehicleMake ?? ""} ${value?.tradeVehicleModel ?? ""}`,
			renderCell: (params) => {
				const { row } = params;
				const { potentialTrade } = row;

				return (
					<PopupWrapper
						text="View Trade Vehicle"
						data={potentialTrade}
						onClick={() => {
							setArrayModalContent({ title: "Trade Vehicle", data: potentialTrade });
						}}
					/>
				);
			},
		},
		{
			...getBaseColumnDefinition(ONE_HUNDRED_FIFTY),
			field: "lastActivity",
			headerName: "Last Activity",
			type: "dateTime",
			valueGetter: (lastActivity) => {
				if (!lastActivity) {
					return "";
				}
				const { creationDateTime } = lastActivity;
				return creationDateTime ? new Date(creationDateTime) : "";
			},
			renderCell: (params) => {
				const { row } = params;
				const { lastActivity } = row;
				if (!lastActivity?.creationDateTime) {
					return "";
				}
				const { icon, title, date } = getLastActivityInfo(lastActivity);
				return (
					<TypographyCell tooltipProps={{ title }} forceTooltip={true} className="hover:cursor-pointer flex">
						<img src={icon} alt={`Prospect Contacted`} className="hover:cursor-pointer mr-[10px]" />
						{date}
					</TypographyCell>
				);
			},
		},
	];
};

const getColumnsDef = ({
	setProspectNoteDialog,
	setArrayModalContent,
}: {
	setProspectNoteDialog: ({ title, data }: IProspectNoteDialogContent) => void;
	setArrayModalContent: ({ title, data }: IArrayDialogContent) => void;
}): GridColDef[] => {
	return [
		...getPreProspectNoteColumnsDef(),
		...getPostProspectNoteColumnsDef({ setProspectNoteDialog, setArrayModalContent }),
	] as GridColDef[];
};

interface ISalesLeadsTableProps {
	rows: Nullable<ISalesLeads[]>;
	handleAddItem: () => void;
	hasNextPage: boolean;
	isFetching: boolean;
	hasEditSalesLeadsAccess: boolean;
}

export default function SalesLeadsTable(props: Readonly<ISalesLeadsTableProps>) {
	const { rows = [], hasNextPage, isFetching, hasEditSalesLeadsAccess, handleAddItem } = props;
	const [prospectNoteDialog, setProspectNoteDialog] = useState<IProspectNoteDialogContent | null>(null);
	const [arrayModalContent, setArrayModalContent] = useState<MappingDialogContent | null>(null);
	const apiRef = useGridApiRef();
	const [sx, setSx] = useState<SxProps>({});
	const [selectedRows, setSelectedRows] = useState<string[]>([]);

	/* useMemos */
	const slotProps = useMemo(
		() => ({
			toolbar: {
				customComponent: (
					<TableCustomComponents
						hasNextPage={hasNextPage}
						isFetching={isFetching}
						text={"Sales Leads data is still loading..."}
					/>
				),
				filter: {
					data: rows ?? [],
					hide: selectedRows?.length > 0,
					disabled: false,
				},
				search: {
					useQuickFilter: true,
					hide: selectedRows?.length > 0,
				},
				expand: {
					hide: true,
				},
				add: {
					onAddAction: handleAddItem,
					hide: selectedRows?.length > 0,
					disabled: !hasEditSalesLeadsAccess,
				},
			},
			noRowsOverlay: {
				title: "No Leads Data",
				className: "!text-montserrat",
			},
			noResultsOverlay: {
				title: "No Lead Data",
				className: "!text-montserrat",
			},
		}),
		[rows, selectedRows, hasNextPage, isFetching, hasEditSalesLeadsAccess]
	);

	/* Hooks*/
	const { customProps } = useCustomProps({ rows: rows ?? [] });

	/* Handlers */
	const handleRowSelected = (newSelection: GridRowSelectionModel) => {
		setSelectedRows(newSelection as string[]);
	};

	/* useEffects */
	useEffect(() => {
		if (!apiRef.current) {
			return undefined;
		}
		setSx(generateSx(apiRef, { yGap: "0px" }));
		return apiRef.current?.subscribeEvent("virtualScrollerContentSizeChange", () => {
			setSx(generateSx(apiRef));
		});
	}, [apiRef]);

	return (
		<>
			<DataSmartTable
				apiRef={apiRef}
				columns={getColumnsDef({ setProspectNoteDialog, setArrayModalContent })}
				rows={rows ?? []}
				headerHeight={DEFAULT_HEADER_HEIGHT}
				maxRows={maxRows}
				getRowClassName={(params) => {
					return clsx({
						"group/stripe even-row": params.indexRelativeToCurrentPage % 2 === 1,
						"group/stripe odd-row": params.indexRelativeToCurrentPage % 2 === 0,
					});
				}}
				enableCustomScrollbars
				disableColumnMenu
				disableRowSelectionOnClick
				checkboxSelection={true}
				isRowSelectable={() => true}
				onRowSelectionModelChange={handleRowSelected}
				columnVisibilityModel={columnVisibilityModel}
				slots={slots}
				slotProps={slotProps}
				hideFooter
				initialState={{
					pinnedColumns: {
						left: [
							GRID_CHECKBOX_SELECTION_COL_DEF.field,
							"_id",
							"prospectType",
							"responseStatus",
							"hasOEMEvent",
							"customerName",
							"prospectCategory",
							"leadDate",
							"updatedAt",
							"prospectStatus",
							"inStoreDate",
							"bdcUser",
							"prospectId",
							"nameRecId",
							"__empty",
						],
					},
				}}
				customProps={customProps}
				sx={sx}
				rowHeight={DEFAULT_ROW_HEIGHT}
				autoHeight={false}
			/>
			<ProspectNoteDialog dialogContent={prospectNoteDialog} handleOnCloseDialog={() => setProspectNoteDialog(null)} />
			<ArrayDialog dialogContent={arrayModalContent} handleOnCloseDialog={() => setArrayModalContent(null)} />
		</>
	);
}
